import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck, MessageSquare } from "lucide-react";

/**
 * DEDICATED SMS Program Terms page — Twilio A2P 10DLC compliance.
 *
 * This URL contains ONLY the SMS Program Terms. It is deliberately separated
 * from the general Terms of Service (/terms) so a Twilio campaign reviewer
 * cannot interpret the SMS opt-in consent as "buried" in general T&Cs — the
 * failure mode that triggered Error 30882. This page is the URL provided to
 * Twilio as the campaign-specific opt-in Terms & Conditions.
 *
 * Structure follows CTIA / TCPA / A2P 10DLC review guidelines with 13
 * numbered sections. Every claim on this page can be verified live at
 * /sms-signup without logging in.
 */
export default function SmsTerms() {
  return (
    <div
      className="max-w-3xl mx-auto p-5 space-y-6 pb-16"
      data-testid="sms-terms-page"
    >
      <div className="flex items-center justify-between">
        <Link
          to="/"
          data-testid="sms-terms-back"
          className="nb-btn nb-btn-secondary !py-2 !px-3"
        >
          <ArrowLeft size={16} strokeWidth={3} /> Home
        </Link>
        <div className="text-xs font-bold text-[#4a4a4a] uppercase tracking-wider">
          Program-specific terms
        </div>
        <div className="w-[70px]" />
      </div>

      <div
        className="nb-card-lg p-6 space-y-3"
        style={{ background: "#d9d4f1" }}
      >
        <div className="inline-flex items-center gap-2 nb-tag !text-xs">
          <ShieldCheck size={12} strokeWidth={3} /> SMS Program Terms only
        </div>
        <h1 className="font-display font-black text-3xl sm:text-4xl">
          SyncBook SMS Program Terms
        </h1>
        <p className="text-sm font-medium text-[#1a1a1a]">
          These are the campaign-specific Terms &amp; Conditions for the
          SyncBook SMS Transaction Logging program. This document governs the
          SMS program only. SyncBook&apos;s general{" "}
          <Link className="underline font-bold" to="/terms">
            Terms of Service
          </Link>{" "}
          and{" "}
          <Link className="underline font-bold" to="/privacy">
            Privacy Policy
          </Link>{" "}
          are separate and apply to use of the SyncBook service overall.
        </p>
        <p className="text-xs font-medium text-[#4a4a4a]">
          Last updated: February 22, 2026.
        </p>
      </div>

      <section
        className="nb-card-lg p-5 space-y-3 text-sm"
        data-testid="sms-terms-body"
      >
        <div>
          <div className="font-display font-black text-[13px]">
            1. Program name &amp; description
          </div>
          <p>
            <b>SyncBook SMS Transaction Logging.</b> A transactional-only
            messaging program that lets consumers text short logging shorthands
            (e.g. <span className="font-mono">-4.50 coffee</span>) to
            SyncBook&apos;s number and receive a text-reply confirmation with
            their updated balance. The program sends no marketing content.
          </p>
        </div>

        <div>
          <div className="font-display font-black text-[13px]">
            2. Consent is optional and not a condition of purchase or service
          </div>
          <p>
            SMS opt-in is <b>entirely optional</b>. Consent to receive text
            messages is <b>not a condition of purchasing any product or
            service, nor a condition of creating or using a SyncBook account</b>.
            Consumers can create a SyncBook account, use every feature
            (transactions, budgets, reconcile, forecasting, CSV import, receipt
            OCR), and subscribe to any paid tier without ever providing a phone
            number or opting in to SMS.
          </p>
        </div>

        <div>
          <div className="font-display font-black text-[13px]">
            3. How to opt in
          </div>
          <p>
            A consumer may opt in from either <b>Profile → Text-in
            transactions</b> inside the SyncBook app, or from the public
            opt-in page at{" "}
            <Link
              to="/sms-signup"
              data-testid="sms-terms-signup-link"
              className="underline font-bold"
            >
              sync-book.app/sms-signup
            </Link>
            . In both places the consumer must (a) enter their mobile number
            and (b) actively check a TCPA consent checkbox that starts
            unchecked. No consent is captured passively or by silence, and
            consent is captured DIRECTLY against these SMS Program Terms — not
            through the general Terms of Service.
          </p>
        </div>

        <div>
          <div className="font-display font-black text-[13px]">
            4. Message frequency
          </div>
          <p>
            Message frequency varies based on how often the consumer uses the
            service. A typical active consumer receives <b>approximately 1–5
            messages per day</b>. There is no fixed schedule of automated
            messages — every message SyncBook sends is a reply to a message the
            consumer first sent.
          </p>
        </div>

        <div>
          <div className="font-display font-black text-[13px]">
            5. Message &amp; data rates may apply
          </div>
          <p>
            <b>Message and data rates may apply.</b> The consumer&apos;s mobile
            carrier may charge them for messages sent or received under
            standard rates. SyncBook itself does not charge for SMS. Consumers
            should contact their carrier if they have questions about their
            plan&apos;s messaging rates.
          </p>
        </div>

        <div>
          <div className="font-display font-black text-[13px]">
            6. How to opt out (STOP)
          </div>
          <p>
            The consumer may cancel SMS at any time by texting <b>STOP</b>,{" "}
            <b>END</b>, <b>QUIT</b>, <b>CANCEL</b>, or <b>UNSUBSCRIBE</b> to
            the SyncBook SMS number, or by unlinking their phone from Profile
            → Text-in transactions inside the app. After STOP is sent the
            consumer will receive one final confirmation message and no
            further SMS will be sent to that number.
          </p>
        </div>

        <div>
          <div className="font-display font-black text-[13px]">7. Help (HELP)</div>
          <p>
            For assistance, the consumer may text <b>HELP</b> or <b>INFO</b> to
            the SyncBook SMS number at any time and will receive a summary of
            supported commands plus support contact information. Consumers may
            also email{" "}
            <a
              className="underline font-bold"
              href="mailto:support@sync-book.app"
            >
              support@sync-book.app
            </a>
            .
          </p>
        </div>

        <div>
          <div className="font-display font-black text-[13px]">
            8. Supported carriers
          </div>
          <p>
            Supported by major US wireless carriers including AT&amp;T,
            Verizon Wireless, T-Mobile, Sprint, Boost, Cricket, MetroPCS, U.S.
            Cellular, and Virgin Mobile USA. T-Mobile is not liable for delayed
            or undelivered messages. Carriers are not liable for delayed or
            undelivered messages.
          </p>
        </div>

        <div>
          <div className="font-display font-black text-[13px]">
            9. No third-party sharing for marketing
          </div>
          <p>
            <b>
              The consumer&apos;s phone number and SMS content will never be
              sold, rented, or shared with third parties, affiliates, or
              marketing partners for marketing, promotional, or lead-generation
              purposes.
            </b>{" "}
            SyncBook shares phone-related data only with Twilio (its SMS
            carrier) to deliver replies. This information is not shared with
            affiliates or third-party marketers under any circumstances.
          </p>
        </div>

        <div>
          <div className="font-display font-black text-[13px]">
            10. Content of messages
          </div>
          <p>
            SMS content is limited to <b>transaction confirmations, balance
            summaries, and STOP/HELP replies</b>. SyncBook does not send
            marketing, promotional, informational, affiliate, lead-generation,
            or third-party content over SMS. This is a transactional-only
            program.
          </p>
        </div>

        <div>
          <div className="font-display font-black text-[13px]">
            11. Privacy
          </div>
          <p>
            See SyncBook&apos;s{" "}
            <Link className="underline font-bold" to="/privacy">
              Privacy Policy
            </Link>{" "}
            for full details on how phone numbers and SMS content are stored
            and processed.
          </p>
        </div>

        <div>
          <div className="font-display font-black text-[13px]">
            12. Contact
          </div>
          <p>
            Questions or complaints about the SMS program:{" "}
            <a
              className="underline font-bold"
              href="mailto:support@sync-book.app"
            >
              support@sync-book.app
            </a>
            .
          </p>
        </div>

        <div data-testid="sms-terms-samples">
          <div className="font-display font-black text-[13px]">
            13. Sample messages
          </div>
          <p>
            The messages consumers receive under this program are limited to
            the categories below. Every outbound message is a reply to a
            message the consumer first sent.
          </p>

          <div className="mt-2 space-y-2">
            <div className="p-2 border-2 border-[#1a1a1a] rounded-lg bg-white">
              <div className="text-[10px] font-bold uppercase tracking-wider text-[#4a4a4a]">
                Confirmation reply — after user texts a transaction
              </div>
              <div className="text-[11px] font-mono mt-1">
                User: <span className="font-bold">-4.50 coffee</span>
              </div>
              <div className="text-[11px] font-mono">
                SyncBook: <span className="font-bold">OK 1: -4.50 coffee | Bal 233.50</span>
              </div>
            </div>

            <div className="p-2 border-2 border-[#1a1a1a] rounded-lg bg-white">
              <div className="text-[10px] font-bold uppercase tracking-wider text-[#4a4a4a]">
                Balance digest — after user texts &ldquo;balance&rdquo;
              </div>
              <div className="text-[11px] font-mono mt-1">
                User: <span className="font-bold">balance</span>
              </div>
              <div className="text-[11px] font-mono whitespace-pre-wrap">
                SyncBook:{" "}
                <span className="font-bold">
                  Main Checking: $233.50{"\n"}Savings: $500.00
                </span>
              </div>
            </div>

            <div className="p-2 border-2 border-[#1a1a1a] rounded-lg bg-white">
              <div className="text-[10px] font-bold uppercase tracking-wider text-[#4a4a4a]">
                Help reply — after user texts &ldquo;HELP&rdquo; or &ldquo;menu&rdquo;
              </div>
              <div className="text-[11px] font-mono mt-1">
                User: <span className="font-bold">HELP</span>
              </div>
              <div className="text-[11px] font-mono">
                SyncBook:{" "}
                <span className="font-bold">
                  SyncBook: &apos;-4.50 coffee&apos;, &apos;+150 pay&apos;. Chain w/
                  &apos;;&apos;: &apos;-4.50 coffee;-12 lunch&apos;. balance | recent |
                  menu | acct:name | force
                </span>
              </div>
            </div>

            <div className="p-2 border-2 border-[#1a1a1a] rounded-lg bg-white">
              <div className="text-[10px] font-bold uppercase tracking-wider text-[#4a4a4a]">
                Opt-out confirmation — after user texts &ldquo;STOP&rdquo;
              </div>
              <div className="text-[11px] font-mono mt-1">
                User: <span className="font-bold">STOP</span>
              </div>
              <div className="text-[11px] font-mono">
                SyncBook (via Twilio):{" "}
                <span className="font-bold">
                  You have successfully been unsubscribed. You will not
                  receive any more messages from this number. Reply START to
                  resubscribe.
                </span>
              </div>
            </div>

            <div className="p-2 border-2 border-[#1a1a1a] rounded-lg bg-white">
              <div className="text-[10px] font-bold uppercase tracking-wider text-[#4a4a4a]">
                Unregistered-phone reply — the very first message from any
                new number (no consent captured)
              </div>
              <div className="text-[11px] font-mono mt-1">
                User: <span className="font-bold">hi</span>
              </div>
              <div className="text-[11px] font-mono">
                SyncBook:{" "}
                <span className="font-bold">
                  This number isn&apos;t linked to a SyncBook account. Log in
                  and add your phone under Profile → SMS.
                </span>
              </div>
            </div>
          </div>

          <p className="mt-2 text-[12px] font-medium text-[#4a4a4a] italic">
            SyncBook does not send unsolicited messages, marketing content,
            informational alerts, promotional offers, affiliate or
            lead-generation content, or third-party content. Every message
            above is triggered by an inbound message from the consumer.
          </p>
        </div>
      </section>

      {/* Related links */}
      <div
        className="nb-card p-4 space-y-2"
        data-testid="sms-terms-related"
      >
        <div className="font-display font-black text-sm flex items-center gap-2">
          <MessageSquare size={14} strokeWidth={3} /> Related documents
        </div>
        <div className="grid sm:grid-cols-3 gap-2">
          <Link
            to="/sms-signup"
            data-testid="sms-terms-link-signup"
            className="nb-btn nb-btn-secondary !py-2 text-xs justify-center"
          >
            /sms-signup (opt-in)
          </Link>
          <Link
            to="/sms-review"
            data-testid="sms-terms-link-review"
            className="nb-btn nb-btn-secondary !py-2 text-xs justify-center"
          >
            /sms-review (compliance)
          </Link>
          <Link
            to="/privacy"
            data-testid="sms-terms-link-privacy"
            className="nb-btn nb-btn-secondary !py-2 text-xs justify-center"
          >
            /privacy
          </Link>
        </div>
      </div>
    </div>
  );
}
